import PropTypes from "prop-types";
import useTranslate from "hooks/use-translate-language";
import { SelectInput, TextInput, PhoneNumberInput } from "components/form_new";
import { FieldArray, Field } from "formik";
import { useSelector } from "react-redux";
import selectors from "selectors";
import { TextField, FormControl, FormHelperText } from "@mui/material";
import { Button } from "components/ui_new/index";
import Autocomplete from "@mui/lab/Autocomplete";
import { DateInputField } from "components/form";
import {
  labelStyles,
  formControlStyles,
  helperTextStyles,
  getAutocompleteStyles
} from "components/form_new/component-styles";
import { STATES } from "utils/enum";

// Returns translation key for insurance type based on index
const getInsuranceTypeKey = index => {
  switch (index) {
    case 0:
      return "primaryInsurance";
    case 1:
      return "secondaryInsurance";
    case 2:
      return "tertiaryInsurance";
    default:
      return "";
  }
};

const EditInsurance = ({ values, setFieldValue, errors }) => {
  const translate = useTranslate();
  const companyInsuranceOptions = useSelector(
    selectors.getPatientInsuranceOptions
  );
  const guarantorRelationOptions = useSelector(
    selectors.getGuarantorRelationOptions
  );
  return (
    <FieldArray
      name="patient_insurances"
      render={arrayHelpers => (
        <div className="profile-panel">
          {values.patient_insurances?.map((_, index) => (
            <div key={index} style={index === 1 ? { marginTop: "20px" } : {}}>
              <div className="profile-panel-content">
                <FormControl
                  sx={formControlStyles}
                  fullWidth
                  error={
                    !!errors.patient_insurances?.[index]?.insurance_company_id
                  }
                >
                  <Autocomplete
                    id={`insurance-box-${index}`}
                    isOptionEqualToValue={(option, value) => {
                      return (
                        option?.insurance_company_id ==
                        value?.insurance_company_id
                      );
                    }}
                    options={companyInsuranceOptions.map(x => ({
                      ...x,
                      insurance_company_id: x.value,
                      insurance_company_name: x.text
                    }))}
                    getOptionLabel={option => option.insurance_company_name}
                    value={values.patient_insurances[index]}
                    onChange={(_, newValue) => {
                      setFieldValue(`patient_insurances.${index}`, {
                        ...values.patient_insurances[index],
                        ...newValue,
                        primary_order: index + 1
                      });
                    }}
                    sx={getAutocompleteStyles(
                      !!errors.patient_insurances?.[index]?.insurance_company_id
                    )}
                    slotProps={{
                      clearIndicator: {
                        tabIndex: 0,
                        "aria-label": `${translate("clear")} ${translate(getInsuranceTypeKey(index))} ${translate("selection")}`,
                        title: `${translate("clear")} ${translate(getInsuranceTypeKey(index))} ${translate("selection")}`,
                        sx: {
                          visibility: "visible"
                        },
                        onKeyDown: event => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setFieldValue(`patient_insurances.${index}`, {
                              ...values.patient_insurances[index],
                              insurance_company_id: "",
                              insurance_company_name: ""
                            });
                          }
                        },
                        onClick: () => {
                          setFieldValue(`patient_insurances.${index}`, {
                            ...values.patient_insurances[index],
                            insurance_company_id: "",
                            insurance_company_name: ""
                          });
                        }
                      }
                    }}
                    renderInput={params => {
                      const errorId = `insurance-${index}-error`;
                      return (
                        <TextField
                          {...params}
                          name={`patient_insurances.${index}.insurance_company_id`}
                          error={
                            !!errors.patient_insurances?.[index]
                              ?.insurance_company_id
                          }
                          inputProps={{
                            ...params.inputProps,
                            'aria-invalid': !!errors.patient_insurances?.[index]
                                ?.insurance_company_id,
                            'aria-describedby': errors.patient_insurances?.[index]
                              ?.insurance_company_id
                              ? errorId
                              : undefined
                          }}
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                              sx: labelStyles
                            }
                          }}
                          label={
                            index === 0
                              ? translate("primaryInsurance")
                              : translate("secondaryInsurance")
                          }
                          variant="outlined"
                        />
                      );
                    }}
                  />
                  {errors.patient_insurances?.[index]?.insurance_company_id && (
                    <FormHelperText
                      id={`insurance-${index}-error`}
                      sx={helperTextStyles}
                    >
                      {errors.patient_insurances[index].insurance_company_id}
                    </FormHelperText>
                  )}
                </FormControl>
                <TextInput
                  name={`patient_insurances.${index}.plan_no`}
                  type="text"
                  label={translate("policyNumber")}
                />
                <TextInput
                  name={`patient_insurances.${index}.group`}
                  type="text"
                  label={translate("groupNumber")}
                />

                <PhoneNumberInput
                  name={`patient_insurances.${index}.insurance_phone`}
                  type="tel"
                  label={translate("insurancePhoneNumber")}
                />

                <Field
                  name={`patient_insurances.${index}.policy_start_date`}
                  component={DateInputField}
                  label={translate("policyStartDate")}
                />
                <Field
                  name={`patient_insurances.${index}.policy_end_date`}
                  component={DateInputField}
                  label={translate("policyEndDate")}
                  disabled
                />
              </div>
              <div
                className="profile-panel-content"
                style={{ marginTop: "20px" }}
              >
                <TextInput
                  name={`patient_insurances.${index}.guarantor_name`}
                  type="text"
                  label={translate("guarantorName")}
                />
                <SelectInput
                  name={`patient_insurances.${index}.guarantor_relation`}
                  component="select"
                  label={translate("guarantorRelationship")}
                  options={guarantorRelationOptions}
                  showPleaseSelect
                />
                <Field
                  name={`patient_insurances.${index}.guarantor_dob`}
                  component={DateInputField}
                  label={translate("guarantorDob")}
                />
              </div>
              <div
                className="profile-panel-content"
                style={{ marginTop: "20px" }}
              >
                <TextInput
                  name={`patient_insurances.${index}.insurance_address.street_address`}
                  type="text"
                  label={translate("insuranceStreetAddress")}
                />
                <TextInput
                  name={`patient_insurances.${index}.insurance_address.street_address2`}
                  type="text"
                  label={translate("insuranceStreetAddress2")}
                />
                <TextInput
                  name={`patient_insurances.${index}.insurance_address.city`}
                  type="text"
                  label={translate("insuranceCity")}
                />
                <SelectInput
                  name={`patient_insurances.${index}.insurance_address.state`}
                  component="select"
                  label={translate("insuranceState")}
                  options={STATES}
                  style={{ height: "100%" }}
                />
                <TextInput
                  name={`patient_insurances.${index}.insurance_address.zip`}
                  type="text"
                  label={translate("insuranceZip")}
                />
                {index > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      justifyContent: "flex-start",
                      alignItems: "center"
                    }}
                  >
                    <Button
                      style={{ width: "100%", maxWidth: "15rem" }}
                      variant="contained"
                      color="primary"
                      id={`delete-insurance-${index}`}
                      type="button"
                      onClick={() => arrayHelpers.remove(index)}
                      aria-label={`${translate("delete")} ${index === 0 ? translate("primaryInsurance") : translate("secondaryInsurance")}`}
                    >
                      {translate("delete")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {values.patient_insurances?.length == 1 && (
            <div
              style={{
                display: "flex",
                flex: 1,
                justifyContent: "flex-end",
                alignItems: "center"
              }}
            >
              <Button
                className="ui_button"
                variant="outlined"
                id="add-secondary-insurance-button"
                onClick={() =>
                  arrayHelpers.push({
                    insurance_company_id: "",
                    insurance_company_name: "",
                    primary_order: 2,
                    policy_number: "",
                    group_number: "",
                    guarantor_relation: "",
                    policy_start_date: "",
                    guarantor_dob: "",
                    guarantor_name: "",
                    insurance_phone: "",
                    insurance_address: {
                      street_address: "",
                      street_address2: "",
                      city: "",
                      state: "",
                      zip: ""
                    }
                  })
                }
              >
                {translate("addSecondaryInsurance")}
              </Button>
            </div>
          )}
        </div>
      )}
    />
  );
};

EditInsurance.propTypes = {
  errors: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired
};

export default EditInsurance;
